import { IItemFilter, IItemFilterHandler } from '../../../controls/filteredItemSetControl';
import { AutoKeyFacet } from '../../AutoKeyFacet';
import { IItemCache } from '../../IItemCacheObserver';
import { folderFacet } from '../../../facets/ItemFacets';
import { photoFacet, videoFacet } from '../../../facets/MediaFacets';
import { oneNoteFacet } from '../../../facets/DocumentFacets';

const itemTypeFilterHandlerKeyFacet = new AutoKeyFacet('itemSetTypeFilterHandler');

/**
 * Creates a filter for items of a particular 'type', as determined by the given predidcate.
 * Since item 'types' can be defined in numerous ways, each filter instance defines a type itself.
 * In order to determine if an item matches a type, refer to the specific filter instance.
 *
 * For example, use `itemFolderTypeFilterHandler` to determine if an item is a folder.
 * And to determine if an item filter is for folders, compare the `.key` to
 * `itemFolderTypeFilterHandler.key`.
 */
export function createItemTypeFilterHandler(filter: IItemFilter): IItemFilterHandler {
  const key = itemTypeFilterHandlerKeyFacet.serializeNext();

  return {
    key: key,
    filter: filter
  };
}

/**
 * Filter handler which determines if an item is a folder.
 */
export const itemFolderTypeFilterHandler: IItemFilterHandler = createItemTypeFilterHandler((
  itemCache: IItemCache,
  props: { itemKey: string; }
) => {
  const folder = itemCache.demandItemFacet(folderFacet, props.itemKey);

  return !!folder;
});

/**
 * Filter handler which determines if an item is not a folder.
 */
export const itemNonFolderTypeFilterHandler: IItemFilterHandler = createItemTypeFilterHandler((
  itemCache: IItemCache,
  props: { itemKey: string; }
) => {
  const folder = itemCache.demandItemFacet(folderFacet, props.itemKey);

  return !folder;
});

/**
 * Filter handler which determines if an item is a photo.
 */
export const itemPhotoTypeFilterHandler: IItemFilterHandler = createItemTypeFilterHandler((
  itemCache: IItemCache,
  props: { itemKey: string; }
) => {
  const photo = itemCache.demandItemFacet(photoFacet, props.itemKey);

  return !!photo;
});

/**
 * Filter handler which determines if an item is a video.
 */
export const itemVideoTypeFilterHandler: IItemFilterHandler = createItemTypeFilterHandler((
  itemCache: IItemCache,
  props: { itemKey: string; }
) => {
  const video = itemCache.demandItemFacet(videoFacet, props.itemKey);

  return !!video;
});

/**
 * Filter handler which determines if an item is an OneNote.
 */
export const itemNonOneNoteFilterHandler: IItemFilterHandler = createItemTypeFilterHandler((
  itemCache: IItemCache,
  props: { itemKey: string; }
) => {
  const oneNote = itemCache.demandItemFacet(oneNoteFacet, props.itemKey);

  return !oneNote;
});
